import { reducerWithInitialState } from 'typescript-fsa-reducers'

import { Screen } from '../api/types'
import * as apiActions from '../api/actions'
import * as actions from './actions'
import { appLanguageSelected, setInitialisedRentalMode } from '../settings/actions'
import { cloneMap, cloneSet } from '../root/functions'

// Collection Item
export interface StoreState {
	readonly screens: Map<number, ScreenState>

	// the audio file path
	readonly audioFilePaths: Map<string, string> // key: fileName, value: filePath
	readonly loadingAudioPaths: Set<string> // key: fileName - used to show activity spinner in UI while audio is being loaded

	// the video file path
	readonly videoFilePaths: Map<string, string>
	readonly loadingVideoPaths: Set<string>
}

interface ScreenState {
	screen?: Screen
	version?: number
	loading: boolean
	error?: Error
}

const INITIAL_STATE: StoreState = {
	screens: new Map(),
	audioFilePaths: new Map(),
	loadingAudioPaths: new Set(),
	videoFilePaths: new Map(),
	loadingVideoPaths: new Set(),
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	// FETCH SCREEN - Network
	.case(apiActions.fetchScreen.started, (state, payload): StoreState => {
		// if downloading screen as part of content syncing, we dont want to cache on redux, just download to disk
		if (payload.isPreCaching) {
			return state
		}

		const updatedScreens = cloneMap(state.screens)
		updatedScreens.set(payload.screenId, { loading: true })

		return {
			...state,
			screens: updatedScreens,
		}
	})
	.case(apiActions.fetchScreen.done, (state, payload): StoreState => {
		if (payload.params.isPreCaching) {
			return state
		}

		const updatedScreens = cloneMap(state.screens)
		updatedScreens.set(payload.params.screenId, {
			screen: payload.result,
			version: payload.params.screenVersion,
			loading: false,
		})

		return {
			...state,
			screens: updatedScreens,
		}
	})
	.case(apiActions.fetchScreen.failed, (state, payload): StoreState => {
		if (payload.params.isPreCaching) {
			return state
		}

		const updatedScreens = cloneMap(state.screens)
		updatedScreens.set(payload.params.screenId, { error: payload.error, loading: false })

		return {
			...state,
			screens: updatedScreens,
		}
	})

	// FETCH SCREEN - Disk
	.case(actions.loadedScreenFromDisk, (state, payload): StoreState => {
		const updatedScreens = cloneMap(state.screens)
		updatedScreens.set(payload.id, { screen: payload.screen, version: payload.version, loading: false })

		return {
			...state,
			screens: updatedScreens,
		}
	})

	// loading audio
	.case(actions.loadAudioPath, (state, payload) => {
		const updatedLoadingAudio = cloneSet(state.loadingAudioPaths)
		updatedLoadingAudio.add(payload.fileName)
		return {
			...state,
			loadingAudioPaths: updatedLoadingAudio,
		}
	})
	// loaded audio file path
	.case(actions.loadedAudioPath, (state, payload) => {
		const updatedAudioFilePaths = cloneMap(state.audioFilePaths)
		const updatedLoadingAudio = cloneSet(state.loadingAudioPaths)
		updatedAudioFilePaths.set(payload.fileName, payload.filePath)
		updatedLoadingAudio.delete(payload.fileName)
		return {
			...state,
			audioFilePaths: updatedAudioFilePaths,
			loadingAudioPaths: updatedLoadingAudio,
		}
	})
	// clear the audio file path
	.case(actions.clearAudioPath, (state, fileName) => {
		const updatedAudioFilePaths = cloneMap(state.audioFilePaths)
		const updatedLoadingAudio = cloneSet(state.loadingAudioPaths)
		updatedAudioFilePaths.delete(fileName)
		updatedLoadingAudio.delete(fileName)
		return {
			...state,
			audioFilePaths: updatedAudioFilePaths,
			loadingAudioPaths: updatedLoadingAudio,
		}
	})

	// loading video
	.case(actions.loadVideoPath, (state, payload) => {
		const updatedLoadingVideo = cloneSet(state.loadingVideoPaths)
		updatedLoadingVideo.add(payload.fileName)
		return {
			...state,
			loadingVideoPaths: updatedLoadingVideo,
		}
	})
	// loaded video file path
	.case(actions.loadedVideoPath, (state, payload) => {
		const updatedVideoFilePaths = cloneMap(state.videoFilePaths)
		updatedVideoFilePaths.set(payload.fileName, payload.filePath)
		const updatedLoadingVideo = cloneSet(state.loadingVideoPaths)
		updatedLoadingVideo.delete(payload.fileName)
		return {
			...state,
			videoFilePaths: updatedVideoFilePaths,
			loadingVideoPaths: updatedLoadingVideo,
		}
	})
	// clear the video file path
	.case(actions.clearVideoPath, (state, fileName) => {
		const updatedVideoFilePaths = cloneMap(state.videoFilePaths)
		updatedVideoFilePaths.delete(fileName)
		const updatedLoadingVideo = cloneSet(state.loadingVideoPaths)
		updatedLoadingVideo.delete(fileName)
		return {
			...state,
			videoFilePaths: updatedVideoFilePaths,
			loadingVideoPaths: updatedLoadingVideo,
		}
	})

	// clear error from loading screen
	.case(actions.clearLoadScreenErrorAction, (state, collectionId): StoreState => {
		const updatedScreens = cloneMap(state.screens)
		updatedScreens.delete(collectionId)
		return {
			...state,
			screens: updatedScreens,
		}
	})

	// LANGUAGE CHANGED
	.case(appLanguageSelected, (): StoreState =>
		// clear all cached files as we do not know what language they are
		({
			...INITIAL_STATE,
		}),
	)

	// RENTAL MODE
	.case(setInitialisedRentalMode, (state, payload): StoreState => {
		if (payload) {
			// return state unchanged as no action required
			return state
		} else {
			// reset all cached content
			return INITIAL_STATE
		}
	})
